import { useCallback, useEffect, useMemo } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import moment from "moment-timezone";
import BreakOvertimeIndicator from "../../../../../../../components/BreakOvertimeIndicator";
import SectionInfo from "../../../../../../../components/SectionInfo";
import ShiftTooltip from "../../../../../../../components/tooltip/ShiftTooltip";
import { createMoreModalDragItem } from "../../DragLayer";
import { useMenuContext } from "../../../../../../../hooks/context/useMenuContext";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { getSectionDurations } from "../../../../../../../helpers/data/getSectionDurations";
import { renderSectionsDuration } from "../../../../../../../helpers/eventdisplay/renderSectionsDuration";

function Item({ event, close }: any) {
    const { canCreateUnassignedShift, canEditUnassignedShift } = usePermissions();
    const { openMenu } = useMenuContext();
    const [{ isDragging }, dragRef, previewRef] = useDrag({
        type: "calendar-event",
        item: () => createMoreModalDragItem({ ...event }, { ...event, type: "unassigned" }) as any,
        canDrag: (canCreateUnassignedShift || canEditUnassignedShift) && !event?.locked,
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    });

    const {
        overtimeDuration,
        breakDuration
    } = useMemo(() => getSectionDurations(event?.sections), [event?.sections]);

    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        openMenu({
            id: "unassigned-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: { ...event, isUnassignedShift: true },
            resource: event?.user,
        });
    }, [openMenu, event]);

    useEffect(() => {
        previewRef(getEmptyImage(), { captureDraggingState: true });
    }, [previewRef]);

    useEffect(() => {
        if (isDragging) {
            close();
        }
    }, [isDragging, close]);

    return (
        <ShiftTooltip data={{ ...event, type: "unassigned" }}>
            <div
                ref={dragRef as any}
                className="
                tw-relative tw-cursor-pointer tw-w-full 
                tw-flex tw-justify-between tw-items-center tw-gap-3 
                tw-border-l-2  tw-border-rose-500 tw-bg-rose-100/35
                tw-py-3 tw-px-3 tw-rounded-sm tw-mb-2
                "
                onContextMenu={onContextMenu}
            >
                <div className="tw-flex tw-flex-col tw-w-full tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-min-w-0 tw-gap-2">
                        <div className="tw-font-bold tw-text-sm tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-truncate">
                            {moment(event.startDate).format("h:mma")}
                            {" - "}
                            {moment(event.endDate).format("h:mma")}
                            <span className="tw-font-normal tw-text-xs tw-text-neutral-600 tw-shrink-0">
                                {"("}
                                {renderSectionsDuration(
                                    moment(event.endDate).diff(moment(event.startDate), "seconds"),
                                    event.sections
                                )}
                                {")"}
                            </span>
                        </div>

                        <BreakOvertimeIndicator
                            breakDuration={breakDuration}
                            overtimeDuration={overtimeDuration}
                            size={17}
                        />
                    </div>

                    <SectionInfo
                        section={event?.sections?.[0]}
                        className="tw-py-1"
                    />
                </div>
                <span
                    className="
                    tw-absolute -tw-top-2 -tw-right-1 tw-min-w-5 tw-h-5 
                    tw-flex tw-items-center tw-justify-center 
                    tw-rounded-full tw-px-1 tw-bg-rose-500 
                    tw-text-white tw-text-xs tw-font-medium"
                >
                    {event?.slots || 1}
                </span>
            </div>
        </ShiftTooltip>
    );
}

export default Item;
